import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setClusterId } from "../../../../redux/slices/vendorClusterSlice";

const stagesData = [
  { name: "Manufacturing", color: "#8B5CF6", completed: true },
  { name: "PDI", color: "#EF4444", completed: true },
  { name: "Dispatch", color: "#F97316", completed: true },
  { name: "Delivered", color: "#10B981", completed: false },
];

const StyledCard = styled(Card)({
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  border: "1px solid #f0f0f0",
  marginBottom: "16px",
  "&:hover": {
    boxShadow: "0 6px 25px rgba(0, 0, 0, 0.12)",
  },
});

const CategoryChip = styled(Chip)({
  backgroundColor: "#FFECDC",
  color: "#FFA04F",
  fontWeight: 600,
  fontSize: "12px",
  height: "24px",
  "& .MuiChip-label": {
    padding: "0 8px",
  },
});

const PriceChip = styled(Chip)({
  backgroundColor: "#0DCE671A", // light green background
  color: "#0DCE67", // green text
  fontWeight: 600,
  fontSize: "14px",
  height: "24px",
  ".MuiChip-label": {
    padding: "0 8px",
  },
});

const ViewButton = styled(Button)({
  backgroundColor: "#4F46E5",
  color: "white",
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 500,
  padding: "8px 24px",
  "&:hover": {
    backgroundColor: "#4338CA",
  },
});

const ProgressContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "8px",
  marginBottom: "8px",
});

const ProgressSegment = styled(Box)(({ color, completed }) => ({
  height: "6px",
  flex: 1,
  backgroundColor: completed ? color : "#E5E7EB",
  borderRadius: "3px",
  minWidth: "80px",
}));

const LotNumber = styled(Typography)({
  fontSize: "48px",
  fontWeight: 700,
  color: "#374151",
  lineHeight: 1,
});

const CompletionProgress = styled(LinearProgress)({
  height: "6px",
  borderRadius: "3px",
  backgroundColor: "#E5E7EB",
  "& .MuiLinearProgress-bar": {
    backgroundColor: "#06B6D4",
    borderRadius: "3px",
  },
});

const ProjectCard = ({ cluster }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formatPrice = (price) => `₹${price.toLocaleString("en-IN")}`;

  return (
    <StyledCard>
      <CardContent sx={{ maxWidth: "100%", padding: "24px" }}>
        <Grid
          container
          spacing={{
            xs: 1,
            md: 3,
          }}
          alignItems="center"
        >
          {/* Left Section */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: {
                  xs: "flex", // enabled on small screens
                  md: "block", // or "initial", "inherit", etc. — disables flex on md+
                },
                justifyContent: {
                  xs: "center",
                  md: "initial", // or remove
                },
                alignItems: {
                  xs: "center",
                  md: "initial", // or remove
                },
                gap: 4,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: {
                    xs: 0, // No margin-bottom on small screens
                    md: "8px", // Apply margin-bottom on md and larger
                  },
                  fontSize: {
                    xs: "14px", // extra-small screens
                    sm: "16px", // small screens
                    md: "18px", // medium screens and up
                  },
                }}
              >
                {cluster?.productType}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#6B7280",
                  marginBottom: {
                    xs: 0, // No margin-bottom on small screens
                    md: "12px", // Apply margin-bottom on md and larger
                  },
                  fontSize: {
                    xs: "14px", // extra-small screens
                    sm: "16px", // small screens
                    md: "18px", // medium screens and up
                  },
                }}
              >
                {new Date(cluster?.jobStartingDate).toLocaleDateString(
                  "en-GB",
                  {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  }
                )}{" "}
                -{" "}
                {new Date(cluster?.jobCompletionDate).toLocaleDateString(
                  "en-GB",
                  {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  }
                )}
              </Typography>
              <Box
                sx={{
                  display: {
                    xs: "flex",
                    md: "flex",
                  },
                  flexDirection: {
                    xs: "column",
                    md: "row",
                  },
                  alignItems: "center",
                  gap: "12px",
                  justifyContent: { xs: "center", sm: "start" },
                }}
              >
                <CategoryChip
                  label={
                    cluster?.serviceType === "0"
                      ? "Fabrication & Printing"
                      : cluster?.serviceType === "1"
                      ? "Fabrication"
                      : cluster?.serviceType === "2"
                      ? "Printing"
                      : "Unknown"
                  }
                />
                <PriceChip label={formatPrice(cluster?.paymentAmount)} />
              </Box>
            </Box>
          </Grid>

          {/* Center Section */}
          <Grid item xs={8} md={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "24px" }}>
              <Box sx={{ textAlign: "center" }}>
                <LotNumber>{cluster?.totalNumberOfJobs}</LotNumber>
                <Typography
                  variant="body2"
                  sx={{ color: "#6B7280", fontWeight: 500 }}
                >
                  Lots
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <ProgressContainer>
                  {stagesData.map((stage, index) => (
                    <div>
                      <ProgressSegment
                        key={index}
                        color={stage.color}
                        completed={stage.completed}
                      />
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <Box
                          sx={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: stage.color,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "#6B7280", fontSize: "11px" }}
                        >
                          {stage.name}
                        </Typography>
                      </Box>
                    </div>
                  ))}
                </ProgressContainer>
              </Box>
            </Box>
          </Grid>

          {/* Right Section */}
          <Grid item xs={4} md={4}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: "16px",
              }}
            >
              <div className="flex flex-col gap-1 justify-center">
                <Box sx={{ width: "100%", maxWidth: "150px" }}>
                  <CompletionProgress variant="determinate" value={20} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#374151",
                      fontWeight: 500,
                      marginTop: "4px",
                      textAlign: "right",
                    }}
                  >
                    {20}% Completed
                  </Typography>
                </Box>
                <ViewButton
                  onClick={() => {
                    dispatch(setClusterId(cluster._id));
                    navigate(
                      `/vendorportal/jobs/cluster?clusterId=${cluster._id}`
                    );
                  }}
                >
                  View
                </ViewButton>
              </div>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </StyledCard>
  );
};

export default function OngoingProjectCard() {
  const { ongoingJobs } = useSelector((state) => state.vendorJob);
  return (
    <Box
      sx={{
        borderRadius: 5,
        backgroundColor: "white",
      }}
    >
      <Box
        px={4}
        py={2}
        sx={{
          backgroundColor: "#F8FAFC",
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          borderBottom: "2px solid #E0E0E0", // add color and style
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Ongoing
        </Typography>
        <Typography>{`${ongoingJobs?.length} cluster`}</Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          padding: "24px",
          borderRadius: 5,
          backgroundColor: "#F5F5F5",
          marginY: 1,
          height: "68vh",
          overflowY: "auto",
        }}
      >
        {ongoingJobs.map((cluster, index) => (
          <ProjectCard key={cluster._id} cluster={cluster} />
        ))}
      </Box>
    </Box>
  );
}
